import { supabase } from './supabase';
import { captureUtmParams, getCapturedUtm } from '../utils/utm';

// Product-funnel analytics. Fire-and-forget client helper that batches events
// and ships them to the track-event edge function. COPPA-safe: only the
// allow-listed funnel events below, flat numeric/string/bool props, and never
// child PII (the edge function sanitizes again as defense in depth).
//
// Why this exists: ad spend is unmeasurable without a funnel you can read.
// Pairs with utils/utm.ts (first-touch attribution) so every stage is
// sliceable by utm_campaign in the funnel_by_campaign view.

export type FunnelEvent =
  | 'landing_view'
  | 'signup_started'
  | 'signup_form_engaged'
  | 'signup_completed'
  | 'email_verified'
  | 'app_open'
  | 'lesson_complete'
  | 'checkout_started'
  | 'checkout_success';

type Props = Record<string, string | number | boolean>;
interface QueuedEvent { name: FunnelEvent; ts: number; props?: Props }

const ANON_KEY = 'hsy.anon-id';
const FLUSH_INTERVAL_MS = 8000;
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const endpoint = supabaseUrl ? `${supabaseUrl}/functions/v1/track-event` : '';

let queue: QueuedEvent[] = [];
let timer: number | null = null;
let accessToken: string | null = null;
let started = false;

function anonId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return 'anon-unavailable';
  }
}

function send(events: QueuedEvent[], keepalive: boolean): void {
  if (!endpoint || !anonKey || events.length === 0) return;
  const body = JSON.stringify({
    anonId: anonId(),
    utm: getCapturedUtm() ?? {},
    events,
  });
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: anonKey,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  // keepalive lets the request survive a page-hide/navigation (the modern
  // sendBeacon that still supports headers, which we need for the gateway).
  fetch(endpoint, { method: 'POST', headers, body, keepalive }).catch(() => {
    // Never break a user flow for observability.
  });
}

/**
 * Record the verification milestone with the known authenticated session.
 * This avoids an auth-listener race where the event could otherwise arrive as
 * anonymous and prevents the database from deduplicating it per account.
 */
export function trackEmailVerified(authToken: string): void {
  try {
    if (!authToken) return;
    const previousToken = accessToken;
    accessToken = authToken;
    send([{ name: 'email_verified', ts: Date.now() }], true);
    accessToken = previousToken;
  } catch {
    // Observability must never interfere with authentication.
  }
}

function flush(keepalive = false): void {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  send(batch, keepalive);
}

/** Begin batching — call once at app start. Idempotent. */
export function startAnalytics(): void {
  if (started) return;
  started = true;
  // Cache the access token so the page-hide flush can attach it synchronously.
  supabase.auth.getSession().then(({ data }) => { accessToken = data.session?.access_token ?? null; });
  supabase.auth.onAuthStateChange((_e, session) => { accessToken = session?.access_token ?? null; });

  timer = window.setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}

/** Record a funnel event. Fire-and-forget — never throws. */
export function track(name: FunnelEvent, props?: Props): void {
  try {
    if (name === 'landing_view') {
      // Landing pages fire this from a child effect, which runs before the
      // App-level captureUtmParams effect — capture here first (idempotent,
      // first touch wins) so the first landing_view carries the context.
      captureUtmParams();
      const ft = getCapturedUtm();
      props = {
        ...(ft?.ref_host ? { ref_host: ft.ref_host } : {}),
        ...(ft?.landing_path ? { landing_path: ft.landing_path } : {}),
        ...props,
      };
    }
    queue.push({ name, ts: Date.now(), props });
    // Milestone events flush immediately. Batching (8s interval) relies on the
    // pagehide/visibilitychange flush to catch anyone who leaves sooner — but
    // that keepalive send is unreliable on fast bounces (esp. mobile Safari),
    // so cold ad traffic that lands and immediately backs out was silently
    // dropped. landing_view + signup_started fire on page arrival and are the
    // funnel's denominator, so they must send on the spot; the checkout/signup
    // events flush for the same reason (a same-tick redirect to Stripe).
    if (
      name === 'landing_view' ||
      name === 'signup_started' ||
      name === 'signup_form_engaged' ||
      name === 'checkout_started' ||
      name === 'signup_completed' ||
      name === 'checkout_success' ||
      name === 'email_verified'
    ) {
      flush(true);
    }
  } catch {
    // ignore
  }
}

/** Test/teardown helper. */
export function _stopAnalytics(): void {
  if (timer) window.clearInterval(timer);
  timer = null;
  started = false;
}
